import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"

import media from "../../styles/media"
import { GridContainer } from "../common"
import { PlayButton, PauseButton } from "../icons"

import { VideoEmbed } from "./VideoEmbed"

const Wrapper = styled(GridContainer)`
  padding: 0;
  grid-column: 1 / -1;

  ${media.tablet`
    border-bottom: 1px solid ${props => props.theme.black};
    padding: 0;
    padding-bottom: 30px;
  `}
`

const VideoWrapper = styled.div`
  grid-column: 1 / -1;
  height: 50vh;

  ${media.tablet`
    grid-column: 1 / span 6;
  `}

  .player {
    height: 100%;
    width: 100%;
  }
`

const VideoList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  grid-column: 1 / -1;

  li {
    border-bottom: 1px solid ${props => props.theme.black};
    display: flex;
    align-items: center;
    padding: 30px 0;

    &:first-of-type {
      padding-top: 0;
    }
  }

  ${media.tablet`
    grid-column: auto / span 6;
  `}
`

const Button = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
  flex: 1;
`

const PlayPauseButton = styled(Button)`
  text-align: right;
  margin-right: 20px;

  &:hover {
    #Stroke-22,
    #Stroke-1 {
      stroke: ${props => props.theme.red};
    }
  }
`

const VideoButton = styled(Button)`
  color: ${props =>
    props.active ? props.theme.black : props.theme.lightGrey3};
  font-size: 16px;
  letter-spacing: 0.07px;
  line-height: 22.68px;
  text-align: left;

  &:hover {
    color: ${props => props.theme.red};
  }

  .subtitle {
    color: ${props => props.theme.lightGrey3};
  }
`

export function VideoGallery({ videos }) {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0)
  const [playingIndex, setPlayingIndex] = useState(null)
  const [player, setPlayer] = useState(null)

  const activeVideo = videos[currentVideoIndex]

  // Every time the player state changes (e.g. video is selected, play/pause button pressed),
  // we need to call out to the native methods on our Vimeo/YouTube player to reflect that state
  // change
  useEffect(() => {
    if (!player) {
      return
    }

    playingIndex === currentVideoIndex ? player.play() : player.pause()
  }, [currentVideoIndex, player, playingIndex])

  function onPlay() {
    setPlayingIndex(currentVideoIndex)
  }

  function onPause() {
    setPlayingIndex(null)
  }

  return (
    <Wrapper>
      <VideoWrapper>
        <VideoEmbed
          url={activeVideo.url}
          onReady={player => setPlayer(player)}
          onPlay={onPlay}
          onPause={onPause}
        />
      </VideoWrapper>

      <VideoList>
        {videos.map((video, index) => (
          <li key={index}>
            <VideoButton
              active={index === currentVideoIndex}
              onClick={() => {
                setCurrentVideoIndex(index)
                setPlayingIndex(null)
              }}
            >
              <span className="title">{video.title}</span>{" "}
              <span className="subtitle">{video.subtitle}</span>
            </VideoButton>

            {player && (
              <PlayPauseButton
                onClick={() => {
                  if (index === currentVideoIndex) {
                    index === playingIndex
                      ? setPlayingIndex(null)
                      : setPlayingIndex(index)

                    return
                  }

                  setPlayingIndex(index)
                  setCurrentVideoIndex(index)
                }}
              >
                {playingIndex === index ? <PauseButton /> : <PlayButton />}
              </PlayPauseButton>
            )}
          </li>
        ))}
      </VideoList>
    </Wrapper>
  )
}

VideoGallery.propTypes = {
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      subtitle: PropTypes.string,
      url: PropTypes.string,
    })
  ),
}
